import React from 'react';
import { useTranslation } from 'react-i18next';

interface VideoMotionControlsProps {
  showVideoMotionAccordion: boolean;
  setShowVideoMotionAccordion: (show: boolean) => void;
  selectedMotionTemplate: string;
  setSelectedMotionTemplate: (template: string) => void;
  selectedMotionTypes: string[];
  setSelectedMotionTypes: (types: string[]) => void;
  motionIntensity: string;
  setMotionIntensity: (intensity: string) => void;
  motionSpeed: number;
  setMotionSpeed: (speed: number) => void;
}

const VideoMotionControls: React.FC<VideoMotionControlsProps> = ({
  showVideoMotionAccordion,
  setShowVideoMotionAccordion,
  selectedMotionTemplate,
  setSelectedMotionTemplate,
  selectedMotionTypes,
  setSelectedMotionTypes,
  motionIntensity,
  setMotionIntensity,
  motionSpeed,
  setMotionSpeed,
}) => {
  const { t } = useTranslation('aiGenerator');
  
  // Handle motion type toggle
  const toggleMotionType = (motionType: string) => {
    const newTypes = selectedMotionTypes.includes(motionType) 
      ? selectedMotionTypes.filter(type => type !== motionType)
      : [...selectedMotionTypes, motionType];
    setSelectedMotionTypes(newTypes);
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <button
        onClick={() => setShowVideoMotionAccordion(!showVideoMotionAccordion)}
        style={{
          width: '100%',
          padding: '12px 16px',
          backgroundColor: showVideoMotionAccordion ? 'rgba(139, 92, 246, 0.1)' : 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: '8px',
          color: 'white',
          fontSize: '14px',
          fontWeight: '500',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'all 0.2s ease'
        }}
      >
        <span>{t('video.motionControls.title')}</span>
        <span style={{ 
          transform: showVideoMotionAccordion ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease',
          fontSize: '12px'
        }}>
          ▼
        </span>
      </button>
      
      {showVideoMotionAccordion && (
        <div style={{ 
          marginTop: '10px', 
          padding: '15px', 
          backgroundColor: 'rgba(15, 15, 15, 0.8)', 
          borderRadius: '8px',
          border: '1px solid rgba(139, 92, 246, 0.2)',
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{ display: 'grid', gap: '20px' }}>
            
            {/* Motion Templates */}
            <div>
              <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#e5e7eb' }}>
                {t('video.motionControls.motionTemplates')}
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                {[
                  { key: 'cinematic', label: t('video.motionControls.cinematic'), desc: t('video.motionControls.cinematicDesc') },
                  { key: 'dynamic', label: t('video.motionControls.dynamic'), desc: t('video.motionControls.dynamicDesc') },
                  { key: 'gentle', label: t('video.motionControls.gentle'), desc: t('video.motionControls.gentleDesc') },
                  { key: 'dramatic', label: t('video.motionControls.dramatic'), desc: t('video.motionControls.dramaticDesc') }
                ].map(template => (
                  <button
                    key={template.key}
                    onClick={() => setSelectedMotionTemplate(selectedMotionTemplate === template.key ? '' : template.key)}
                    style={{
                      background: selectedMotionTemplate === template.key ? '#8b5cf6' : '#333',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '6px',
                      padding: '12px 8px',
                      color: 'white',
                      fontSize: '11px',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <div style={{ fontWeight: '600' }}>{template.label}</div>
                    <div style={{ fontSize: '9px', color: '#aaa' }}>{template.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Camera Movement */}
            <div>
              <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#e5e7eb' }}>
                {t('video.motionControls.cameraMovement')}
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px' }}>
                {[
                  { type: 'pan_left', label: '📷', desc: t('video.motionControls.panLeft'), icon: '⬅️' },
                  { type: 'pan_right', label: '📷', desc: t('video.motionControls.panRight'), icon: '➡️' },
                  { type: 'zoom_in', label: '🔍', desc: t('video.motionControls.zoomIn'), icon: '➕' },
                  { type: 'zoom_out', label: '🔍', desc: t('video.motionControls.zoomOut'), icon: '➖' },
                  { type: 'tilt_up', label: '📐', desc: t('video.motionControls.tiltUp'), icon: '⬆️' },
                  { type: 'tilt_down', label: '📐', desc: t('video.motionControls.tiltDown'), icon: '⬇️' }
                ].map((motion) => (
                  <button
                    key={motion.type}
                    onClick={() => toggleMotionType(motion.type)}
                    style={{
                      background: selectedMotionTypes.includes(motion.type) ? '#8b5cf6' : '#374151',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '6px',
                      padding: '8px 4px',
                      color: 'white',
                      fontSize: '10px',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '2px'
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>{motion.icon}</span>
                    <span style={{ fontSize: '9px', fontWeight: '600' }}>{motion.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Motion Types */}
            <div>
              <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#e5e7eb' }}>
                {t('video.motionControls.motionTypes')}
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {[
                  { type: 'rotation', label: t('video.motionControls.rotation') },
                  { type: 'parallax', label: t('video.motionControls.parallax') },
                  { type: 'dolly', label: t('video.motionControls.dolly') },
                  { type: 'crane', label: t('video.motionControls.crane') },
                  { type: 'tracking', label: t('video.motionControls.tracking') },
                  { type: 'handheld', label: t('video.motionControls.handheld') }
                ].map((motion) => (
                  <button
                    key={motion.type}
                    onClick={() => toggleMotionType(motion.type)}
                    style={{
                      background: selectedMotionTypes.includes(motion.type) ? '#8b5cf6' : '#333',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      color: 'white',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontWeight: selectedMotionTypes.includes(motion.type) ? '600' : '400'
                    }}
                  >
                    {motion.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Motion Intensity - Button style */}
            <div>
              <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#e5e7eb' }}>
                {t('video.motionControls.motionIntensity')}
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                {[
                  { value: 'subtle', label: t('video.motionControls.subtle') },
                  { value: 'moderate', label: t('video.motionControls.moderate') },
                  { value: 'dramatic', label: t('video.motionControls.dramaticIntensity') }
                ].map(intensity => (
                  <button
                    key={intensity.value}
                    onClick={() => setMotionIntensity(intensity.value)}
                    style={{
                      background: motionIntensity === intensity.value ? '#8b5cf6' : '#333',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '6px',
                      padding: '12px 16px',
                      color: 'white',
                      fontSize: '13px',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s ease',
                      fontWeight: motionIntensity === intensity.value ? '600' : '400'
                    }}
                  >
                    {intensity.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Motion Speed */}
            <div>
              <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#e5e7eb' }}>
                {t('video.motionControls.motionSpeed', { speed: motionSpeed })}
              </label>
              <input
                type="range"
                min="0.5"
                max="2.0"
                step="0.1"
                value={motionSpeed}
                onChange={(e) => setMotionSpeed(Number(e.target.value))}
                style={{
                  width: '100%',
                  height: '8px',
                  borderRadius: '4px',
                  backgroundColor: '#8b5cf6',
                  outline: 'none',
                  cursor: 'pointer',
                  appearance: 'none'
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#999', marginTop: '4px' }}>
                <span>0.5x</span>
                <span>1.0x</span>
                <span>2.0x</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoMotionControls;